<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  fetchDistricts, collectDistrict, upgradeDistrict,
  type District, type SeenDistrict, type OwnedDistrict,
} from '../game/territory-api';
import { districtArt, crestArt } from '../game/art';
import { confirmAction } from '../game/confirm';
import { toast } from '../game/toast';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import { districtAccent } from '../game/district-accent';
import { t, tc } from '../i18n';

const emit = defineEmits<{ (e: 'updated'): void; (e: 'goto-map', districtId?: string): void }>();

const loading = ref(true);
// One shared ticker drives every ward countdown. It ticks each SECOND now —
// the 30s cadence read as a frozen clock (QA 18.08 item 17).
const nowTick = ref(Date.now());
const _tick = window.setInterval(() => { nowTick.value = Date.now(); }, 1_000);
onUnmounted(() => clearInterval(_tick));
/** M:SS under an hour of drama, else "Xh Ym" — always visibly ticking. */
function fmtLeft(untilIso: string): string {
  const left = Math.max(0, new Date(untilIso).getTime() - nowTick.value);
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  const sec = Math.floor((left % 60_000) / 1_000);
  return h > 0 ? `${h}h ${m}m` : `${m}:${String(sec).padStart(2, '0')}`;
}
/** The wait on a ward whose rent has not yet reached its first coin. Empty
 *  once it has, or while a siege is the reason Collect is dead (the card is
 *  already carrying the siege clock in that case). */
function collectIn(d: OwnedDistrict): string {
  if (d.readyToCollect || d.underSiege || !d.collectableAt) return '';
  const left = new Date(d.collectableAt).getTime() - nowTick.value;
  return left > 0 ? fmtLeft(d.collectableAt) : '';
}
function siegeLeft(d: OwnedDistrict): string {
  return d.siegeEndsAt ? fmtLeft(d.siegeEndsAt) : '';
}
/** Quiet wards carry the raiders' return clock (item 17). */
function nextRaidIn(d: OwnedDistrict): string {
  return d.nextSiegeAt ? fmtLeft(d.nextSiegeAt) : '';
}
const error = ref('');
const districts = ref<District[]>([]);
const busy = ref<string | null>(null);          // districtId currently mutating (collect/upgrade)
const collectAllBusy = ref(false);
const collectPopup = ref<{ id: string; amount: number } | null>(null);

async function refresh() {
  try { districts.value = await fetchDistricts(); }
  catch (e) { error.value = (e as Error).message; }
}

onMounted(async () => {
  await refresh();
  loading.value = false;
});

// ── Intel strip ──────────────────────────────────────────────────────────
const ownedDistricts = computed(() =>
  districts.value.filter((d): d is OwnedDistrict => d.visibility === 'owned'));
const totalCount = computed(() => districts.value.length || 8);
const turfsHeld = computed(() => ownedDistricts.value.length);
const scoutedCount = computed(() => districts.value.filter((d) => d.visibility !== 'unknown').length);
/** Unread wards collapse into ONE band. A fresh account holds 0 and has scouted 1,
 *  so the wall was eleven identical frost stumps — a row of clones that read as a
 *  broken grid, not as fog of war. One band says the same thing once. */
const knownDistricts = computed(() => districts.value.filter((d) => d.visibility !== 'unknown'));
const unknownCount = computed(() => districts.value.filter((d) => d.visibility === 'unknown').length);
const takeHome = computed(() => ownedDistricts.value.reduce((s, d) => s + d.income, 0));
const readyDistricts = computed(() => ownedDistricts.value.filter((d) => d.readyToCollect));
const readyCashTotal = computed(() => readyDistricts.value.reduce((s, d) => s + d.pendingIncome, 0));

/** Tier as a carved numeral. Roman because the crown of a stone is an inscription,
 *  and "Tier 4" in a pill was one of the card grid's tells. */
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
const roman = (n?: number) => (n && ROMAN[n]) || String(n ?? '?');

function ownerLabel(o: SeenDistrict['owner']) {
  return o === 'rival' ? t('districts.rivalClan') : t('districts.upForGrabs');
}
function safePct(d: OwnedDistrict): number {
  return Math.min(100, Math.round((d.pendingIncome / Math.max(1, d.storageCap)) * 100));
}

async function onCollect(d: OwnedDistrict) {
  busy.value = d.districtId; error.value = '';
  try {
    const res = await collectDistrict(d.districtId);
    toast(t('districts.collected', { amount: res.collected.toLocaleString(), name: d.name }), { icon: cashIcon });
    collectPopup.value = { id: d.districtId, amount: res.collected };
    window.setTimeout(() => {
      if (collectPopup.value?.id === d.districtId) collectPopup.value = null;
    }, 1600);
    await refresh();
    emit('updated');                              // header refetches cash
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function onUpgrade(d: OwnedDistrict) {
  // Raising a ward spends silver, and it used to spend it on the first click
  // with nothing said about the price (QA 86cb1qvpt). State the cost and what
  // it buys, then charge.
  const ok = await confirmAction({
    kicker: t('districts.upgradeKicker'),
    title: t('districts.upgradeTitle', { name: d.name, level: d.level + 1 }),
    icon: crestArt(d.name) ?? cashIcon,
    rows: [
      { label: t('districts.upgradeTributeLabel'), value: t('districts.upgradeTributeValue', { from: d.level, to: d.level + 1 }), tone: 'dim' },
      { label: t('districts.upgradeCostLabel'), value: (d.upgradeCost ?? 0).toLocaleString(), icon: cashIcon, tone: 'gold' },
    ],
    note: t('districts.upgradeNote'),
    confirmLabel: t('districts.upgradeConfirm'),
  });
  if (!ok) return;
  busy.value = d.districtId; error.value = '';
  try {
    const up = await upgradeDistrict(d.districtId);
    toast(t('districts.upgraded', { name: d.name, level: up.level, rent: up.spent.toLocaleString() }),
      { icon: crestArt(d.name) ?? cashIcon });
    await refresh();
    emit('updated');                              // header refetches cash
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

/** Sweep every ready-to-collect turf in one go, then a single refresh + emit. */
async function onCollectAll() {
  collectAllBusy.value = true; error.value = '';
  try {
    let total = 0;
    for (const d of readyDistricts.value) {
      total += (await collectDistrict(d.districtId)).collected;
    }
    if (total > 0) toast(t('districts.collectedAll', { amount: total.toLocaleString() }), { icon: cashIcon });
    await refresh();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { collectAllBusy.value = false; }
}
</script>

<template>
  <!-- ══ THE WARD FRIEZE ══════════════════════════════════════════════════════
       WAS a 16:9 banner over a chip strip over `grid-cols-1/2/3/4` of 
       cards with a fog state — Omerta's district list, unchanged but recoloured,
       and the single worst offender in the build at 23 generic-UI tells.

       NOW twelve carved ward-stones standing in a row you walk along. A ward is a
       piece of GROUND, and ground in this world is claimed by raising a stone on
       it and cutting your name in: tier at the crown, the holder's crest set into
       the face, the take cut into the foot. Three states are three states of the
       STONE, not three variants of a card:
         unknown → frost-encased, the carving unreadable under ice
         seen    → carved and legible, but somebody else's name is on it
         owned   → the carving lit from within by aurora
  ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="wf">
    <!-- the tally beam: what the chip strip and the banner used to say, cut into
         one carved beam instead of floating in three pills above a hero image -->
    <div class="wf-beam">
      <div class="wf-tally">
        <span class="wf-tally-k">{{ t('districts.groundHeld') }}</span>
        <span class="wf-tally-v">{{ turfsHeld }}<i>/{{ totalCount }}</i></span>
      </div>
      <span class="wf-beam-sep" aria-hidden="true"></span>
      <div class="wf-tally">
        <span class="wf-tally-k">{{ t('districts.scouted') }}</span>
        <span class="wf-tally-v">{{ scoutedCount }}<i>/{{ totalCount }}</i></span>
      </div>
      <span class="wf-beam-sep" aria-hidden="true"></span>
      <div class="wf-tally">
        <span class="wf-tally-k">{{ t('districts.takeHome') }}</span>
        <span class="wf-tally-v wf-tally-v--coin">{{ takeHome.toLocaleString() }}<i>{{ t('common.silver') }}{{ t('districts.perHour') }}</i></span>
      </div>
      <div class="wf-beam-gap"></div>
      <button
        v-if="readyDistricts.length"
        class="btn-forge btn-forge--rune btn-forge--sm"
        :disabled="collectAllBusy"
        @click="onCollectAll"
      >{{ collectAllBusy ? t('districts.collecting') : t('districts.collectAll', { amount: readyCashTotal.toLocaleString() }) }}</button>
    </div>

    <p v-if="error" class="wf-error">{{ error }}</p>
    <p v-if="loading" class="wf-loading">{{ t('districts.loading') }}</p>

    <!-- THE ROLL OF WARDS — one full-width banner per ward, like a strategy
         game's territory list: the ward's own painting floods the left third and
         dissolves; name and holder in the middle; figures and actions on fixed
         right-hand columns, so every row lines up by construction. -->
    <div v-else class="wf-list">
      <article
        v-for="d in knownDistricts" :key="d.districtId"
        class="wf-band"
        :class="[`is-${d.visibility}`, { 'is-ready': d.visibility === 'owned' && d.readyToCollect, 'is-besieged': d.visibility === 'owned' && d.underSiege }]"
        :style="{ '--ward': districtAccent(d.districtId) }"
      >
        <span class="wf-art" aria-hidden="true">
          <img v-if="districtArt(d.name)" :src="districtArt(d.name)" alt="" />
        </span>
        <span class="wf-crest"><img v-if="crestArt(d.name)" :src="crestArt(d.name)" alt="" /></span>
        <div class="wf-main">
          <p class="wf-holder">{{ 'tier' in d ? t('city.tier', { n: roman(d.tier) }) + ' · ' : '' }}{{ d.visibility === 'owned' ? (d.underSiege ? t('districts.heldUnderSiege') : t('districts.heldByYou')) : `${d.family} · ${ownerLabel(d.owner)}` }}</p>
          <p v-if="d.visibility === 'owned' && d.underSiege" class="wf-siege">
            {{ t('districts.siegeGatePrefix') }} <b>{{ siegeLeft(d) }}</b> {{ t('districts.siegeGateSuffix') }}
            <template v-if="d.tilesAway != null"> · {{ tc('districts.tilesAway', d.tilesAway) }}</template>
          </p>
          <p v-else-if="d.visibility === 'owned' && d.nextSiegeAt" class="wf-nextraid">
            {{ t('districts.nextRaidIn') }} <b class="tabular-nums">{{ nextRaidIn(d) }}</b>
            <template v-if="d.tilesAway != null"> · {{ tc('districts.tilesAway', d.tilesAway) }}</template>
          </p>
          <!-- A ward you do not hold: how far you have to walk to reach it is
               the number that decides anything from this screen, and only owned
               wards were carrying it (QA 86cb8zrgn). -->
          <p v-else-if="d.visibility === 'seen' && d.tilesAway != null" class="wf-nextraid">
            {{ tc('districts.tilesAway', d.tilesAway) }}
          </p>
          <h2 class="wf-name" :class="{ 'gilt-text': d.visibility === 'owned' }">{{ d.name }}</h2>
        </div>
        <div class="wf-stats">
          <template v-if="d.visibility === 'owned'">
            <div class="wf-cut"><span class="wf-cut-k">{{ d.racket }}</span><span class="wf-cut-v">{{ t('districts.lv', { level: d.level, max: d.levelMax }) }}</span></div>
            <div class="wf-cut"><span class="wf-cut-k">{{ t('districts.rent') }}</span><span class="wf-cut-v wf-cut-v--coin">{{ d.income }}<i>{{ t('districts.perHour') }}</i></span></div>
            <div class="bar-shield" :style="{ '--sh-pct': safePct(d), '--sh-tone': 'var(--color-gold)', '--sh-boards': 8 }"><div class="bar-shield-fill"></div></div>
          </template>
          <div v-else class="wf-cut"><span class="wf-cut-k">{{ t('districts.garrison') }}</span><span class="wf-cut-v">{{ d.defense }}</span></div>
        </div>
        <div class="wf-act">
          <!-- a besieged ward's ONLY urgent action is answering the raiders —
               route straight to the map defence instead of Collect/Raise -->
          <template v-if="d.visibility === 'owned' && d.underSiege">
            <button class="btn-forge btn-forge--sm wf-defend" @click="emit('goto-map', d.districtId)">{{ t('districts.breakSiege') }}</button>
          </template>
          <template v-else-if="d.visibility === 'owned'">
            <button class="btn-forge btn-forge--rune btn-forge--sm" :disabled="busy === d.districtId || !d.readyToCollect" @click="onCollect(d)">{{ busy === d.districtId ? '…' : t('districts.collect') }}</button>
            <!-- Collect greys out until the rent reaches its first whole coin,
                 and said nothing about how long that takes (QA 86cb8zjzx). -->
            <span v-if="collectIn(d)" class="wf-collectin">{{ t('districts.collectIn', { time: collectIn(d) }) }}</span>
            <button v-if="d.upgradeCost" class="btn-forge btn-forge--sm" :disabled="busy === d.districtId" :title="t('districts.upgradeTooltip', { cost: d.upgradeCost.toLocaleString() })" @click="onUpgrade(d)">{{ t('districts.raise') }}</button>
            <!-- level 6 is the roof — say so instead of silently dropping the button (item 15) -->
            <span v-else-if="d.maxLevel" class="wf-maxlvl">{{ t('districts.maxLevelReached') }}</span>
          </template>
          <template v-else>
            <span v-if="d.locked" class="wf-locked">{{ t('districts.sealed', { level: d.unlockLevel }) }}</span>
            <button v-else class="btn-forge btn-forge--sm" @click="emit('goto-map', d.districtId)">{{ t('districts.takeOnMap') }}</button>
          </template>
          <span v-if="collectPopup?.id === d.districtId" class="wf-pop">+{{ collectPopup.amount.toLocaleString() }}</span>
        </div>
      </article>
    </div>

    <!-- BEYOND THE ICE — every unread ward, said once -->
    <div v-if="!loading && unknownCount" class="wf-beyond">
      <span class="wf-beyond-stones" aria-hidden="true"><i v-for="k in Math.min(unknownCount, 9)" :key="k"></i></span>
      <div class="wf-beyond-txt">
        <h2>{{ tc('districts.unreadStones', unknownCount) }}</h2>
        <p>{{ t('districts.empty') }}</p>
      </div>
      <button class="btn-forge" @click="emit('goto-map')">{{ t('districts.walkIce') }}</button>
    </div>
  </div>
</template>

<style scoped>
/* raiders at the gate: the band smoulders, the clock line reads like a wound */
.wf-band.is-besieged { box-shadow: inset 0 0 0 1px rgba(224, 82, 74, 0.55), inset 0 0 34px rgba(120, 20, 16, 0.25), 0 10px 26px rgba(0, 0, 0, 0.5); }
.wf-siege {
  margin-top: 2px;
  font-family: var(--font-label, sans-serif);
  font-size: 11px; letter-spacing: 0.05em;
  color: #e0938c;
}
.wf-siege b { color: #f4b8b2; }
/* the quiet ward's clock — same spec line grammar, cold ink instead of blood */
.wf-nextraid {
  margin-top: 2px;
  font-family: var(--font-label, sans-serif);
  font-size: 11px; letter-spacing: 0.05em;
  color: var(--color-text-dim, #9a8f7a);
}
.wf-nextraid b { color: var(--color-text, #ece3cf); }
/* level 6 is the roof (item 15) — a carved plate where the Raise button sat */
.wf-maxlvl {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--color-gold, #c9a84c);
  padding: 5px 10px;
  border: 1px solid color-mix(in srgb, var(--color-gold, #c9a84c) 45%, transparent);
}
.wf-defend { --forge-glow: rgba(224, 82, 74, 0.5); }

/* the beam: carved timber, the tallies cut straight into it */
.wf-beam {
  display: flex; align-items: center; gap: 0 18px; flex-wrap: wrap;
  padding: 12px 18px;
  margin-bottom: 1.35rem;
  background: var(--surface-beam);
  background-size: var(--surface-beam-size);
  background-blend-mode: var(--surface-beam-blend);
  box-shadow:
    var(--rim-ice),
    inset 0 3px 0 rgba(0, 0, 0, 0.5), inset 0 -3px 0 rgba(0, 0, 0, 0.5),
    0 10px 26px rgba(0, 0, 0, 0.5);
}
.wf-beam-gap { flex: 1 1 auto; }
.wf-beam-sep {
  width: 1px; align-self: stretch; margin: 2px 0;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.6) 30% 70%, transparent);
  box-shadow: 1px 0 0 rgba(207, 232, 255, 0.09);
}
.wf-tally { display: flex; flex-direction: column; gap: 2px; }
.wf-tally-k {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.09em; text-transform: uppercase;
  color: #9db0c6;
}
.wf-tally-v {
  font-family: var(--font-heading, serif); font-size: 19px; line-height: 1;
  color: #d3dfee;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85), 0 1px 0 rgba(207, 232, 255, 0.10);
}
.wf-tally-v i {
  font-style: normal; font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #9db0c6; margin-left: 4px;
}
.wf-tally-v--coin { color: var(--color-gold-bright); }

.wf-error { color: var(--color-error); font-size: 13px; margin-bottom: 0.75rem; }
.wf-loading { color: #9db0c6; font-family: var(--font-body, serif); }


.wf-error { color: var(--color-error); font-size: 13px; margin-bottom: 0.75rem; }
.wf-loading { color: #9db0c6; font-family: var(--font-body, serif); }

/* ── THE ROLL — banner strips ───────────────────────────────────────────────── */
.wf-list { display: flex; flex-direction: column; gap: 0.8rem; }
.wf-band {
  --ward: #888;
  position: relative;
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr) 175px 172px;
  align-items: center;
  gap: 0 16px;
  min-height: 104px;
  padding: 12px 16px 12px 0;
  overflow: hidden;
  border: 1px solid #04060a;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  box-shadow:
    inset 0 0 0 1px rgba(201, 165, 88, 0.22),
    inset 0 1px 0 rgba(245, 218, 150, 0.10),
    0 10px 26px rgba(0, 0, 0, 0.5);
}
/* the painting floods the left and DISSOLVES into the timber — masked, not framed */
.wf-art { position: absolute; inset: 0 auto 0 0; width: 300px; }
.wf-art img {
  width: 100%; height: 100%; object-fit: cover;
  -webkit-mask-image: linear-gradient(90deg, #000 46%, transparent 96%);
  mask-image: linear-gradient(90deg, #000 46%, transparent 96%);
}
.wf-crest {
  position: relative; z-index: 1;
  grid-column: 1;
  justify-self: end;
  display: grid; place-items: center;
  width: 56px; height: 56px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken), inset 0 0 12px color-mix(in srgb, var(--ward) 24%, transparent);
}
.wf-crest img { width: 76%; height: 76%; object-fit: contain; }
.wf-main { position: relative; z-index: 1; min-width: 0; }
.wf-holder {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.05em; text-transform: uppercase;
  color: #bccbdb;
}
.wf-name {
  font-family: var(--font-heading, serif);
  font-size: clamp(19px, 1.9vw, 24px); line-height: 1.05;
  color: #dbe4f0;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.9);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.wf-name.gilt-text { text-shadow: none; }

.wf-stats { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 4px; }
.wf-cut {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  padding: 2px 0;
}
.wf-cut-k {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.05em; text-transform: uppercase;
  color: #bccbdb;
}
.wf-cut-v { font-family: var(--font-heading, serif); font-size: 15px; color: #d7e0ec; }
.wf-cut-v--coin { color: var(--color-gold-bright); }
.wf-cut-v i { font-style: normal; font-size: 9px; color: #a4b4c7; }

.wf-act {
  position: relative; z-index: 1;
  display: flex; flex-direction: column; gap: 6px; align-items: stretch;
}
.wf-collectin {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #8fa0b3; text-align: center;
  font-variant-numeric: tabular-nums;
}
.wf-locked {
  font-family: var(--font-label, sans-serif);
  font-size: 9.5px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #8fa0b3; text-align: center; padding: 8px 0;
}

/* states: yours is lit and warm; rival cold; the accent rides the left edge */
.wf-band::before {
  content: ''; position: absolute; inset: 0 auto 0 0; width: 3px; z-index: 2;
  background: color-mix(in srgb, var(--ward) 75%, transparent);
  box-shadow: 1px 0 0 rgba(0, 0, 0, 0.5);
}
.is-owned {
  box-shadow:
    inset 0 0 0 1px rgba(201, 165, 88, 0.4),
    inset 0 1px 0 rgba(245, 218, 150, 0.16),
    inset 0 0 40px rgba(217, 135, 60, 0.07),
    0 10px 26px rgba(0, 0, 0, 0.5);
}
.is-owned .wf-art img { filter: saturate(1.1) brightness(1.05); }
.is-seen .wf-art img { filter: saturate(0.7) brightness(0.8); }
.is-ready { animation: wf-ready 2.6s ease-in-out infinite; }
@keyframes wf-ready {
  0%, 100% { filter: none; }
  50% { filter: drop-shadow(0 0 16px rgba(227, 200, 120, 0.4)); }
}

@media (max-width: 940px) {
  /* The fourth column has to go at this width, and the stats used to go with
     it — `display: none` took Lv, Rent, Garrison and the collect countdown off
     every tablet and phone, which is most of the card's actual content. The
     band grows a second row instead and the stats ride under the name as a
     strip, so nothing is dropped; only the shape changes. */
  .wf-band {
    grid-template-columns: 120px minmax(0, 1fr) 150px;
    grid-template-areas:
      'crest main  act'
      'crest stats act';
    padding-right: 12px;
  }
  .wf-art { width: 190px; }
  .wf-crest { grid-area: crest; }
  .wf-main { grid-area: main; }
  .wf-act { grid-area: act; }
  .wf-stats {
    grid-area: stats;
    flex-direction: row; flex-wrap: wrap; align-items: baseline;
    gap: 2px 14px;
  }
  .wf-cut { justify-content: flex-start; gap: 6px; padding: 0; }
  .wf-stats .bar-shield { flex: 1 1 64px; min-width: 64px; }
}
/* Phone: the desktop row grid starved the text to a one-letter-per-line
   column. The band becomes a vertical card — the painting is a top strip
   dissolving DOWN into the timber, the crest rides its corner, and name and
   actions get the full width. */
@media (max-width: 560px) {
  .wf-band {
    grid-template-columns: minmax(0, 1fr);
    /* one column: drop the two-row areas above and let the children stack in
       DOM order — art, name, stats, actions */
    grid-template-areas: none;
    gap: 6px;
    min-height: 0;
    padding: 0 0 12px;
  }
  .wf-crest, .wf-main, .wf-stats, .wf-act { grid-area: auto; }
  .wf-stats { padding: 0 14px; gap: 2px 16px; }
  .wf-art { position: relative; inset: auto; grid-row: 1; width: 100%; height: 104px; }
  .wf-art img {
    -webkit-mask-image: linear-gradient(180deg, #000 55%, transparent 98%);
    mask-image: linear-gradient(180deg, #000 55%, transparent 98%);
  }
  .wf-crest {
    position: absolute; top: 10px; right: 10px;
    grid-column: auto; justify-self: auto;
    width: 46px; height: 46px;
  }
  .wf-main { padding: 0 14px; }
  .wf-act { flex-direction: row; flex-wrap: wrap; padding: 0 14px; }
  .wf-act .btn-forge { flex: 1 1 auto; }
  .wf-locked { text-align: left; padding: 2px 0; }
}

.wf-pop {
  position: absolute; left: 50%; top: 30%;
  transform: translateX(-50%);
  font-family: var(--font-heading, serif); font-size: 20px;
  color: var(--color-gold-bright);
  text-shadow: 0 0 14px rgba(227, 200, 120, 0.7), 0 2px 4px rgba(0, 0, 0, 0.9);
  animation: wf-pop 1.6s ease-out forwards;
  pointer-events: none;
}
@keyframes wf-pop {
  0% { opacity: 0; transform: translate(-50%, 6px); }
  18% { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%, -30px); }
}

/* ── BEYOND THE ICE — the fog of war as one composed band ────────────────────── */
.wf-beyond {
  display: flex; align-items: center; gap: 22px; flex-wrap: wrap;
  margin-top: 1.4rem;
  padding: 18px 22px;
  background:
    linear-gradient(180deg, rgba(150, 196, 232, 0.07), rgba(120, 172, 214, 0.12)),
    var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}
.wf-beyond-stones { display: flex; align-items: flex-end; gap: 7px; height: 52px; }
.wf-beyond-stones i {
  display: block; width: 17px;
  clip-path: var(--clip-stele);
  background: linear-gradient(180deg, #39424e, #232b35);
  opacity: 0.75;
  filter: blur(0.4px);
}
.wf-beyond-stones i:nth-child(3n) { height: 100%; }
.wf-beyond-stones i:nth-child(3n+1) { height: 72%; }
.wf-beyond-stones i:nth-child(3n+2) { height: 86%; }
.wf-beyond-txt { flex: 1 1 240px; }
.wf-beyond-txt h2 {
  font-family: var(--font-heading, serif); font-size: 17px;
  color: #cfdae8;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85);
}
.wf-beyond-txt p {
  font-family: var(--font-body, serif); font-style: italic;
  font-size: 12.5px; color: #a8b4c4; margin-top: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .is-ready { animation: none; }
}
</style>
